import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AuthProvider, Me } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { PROVIDER_LABEL, linkProvider, signInWith, unlinkProvider } from "../lib/auth";
import { MailIcon } from "./Icons";
import { useHealth } from "../lib/queries";
import { signIn } from "@pagemates/shared/copy";

const txt = signIn.buttons;

/*
 * One button per sign-in provider the server offers (Discord always; Google
 * and Apple when configured). Shown on the landing and invite pages.
 */
export function SignInButtons({ next, verb = txt.continueWith, disabled, assume = ["discord"] }:
  { next: string | (() => string); verb?: string; disabled?: boolean; assume?: AuthProvider[] }) {
  const health = useHealth();
  // `assume` is what shows until the server answers; the prebuilt sign-in page assumes all three.
  const providers: AuthProvider[] = health.data?.providers ?? assume;
  return (
    <div className="signin">
      {providers.map((p) => (
        <button key={p} type="button" className={`btn block prov ${p}`} disabled={disabled} onClick={() => void signInWith(p, typeof next === "function" ? next() : next)}>
          <ProviderIcon p={p} />{verb} {PROVIDER_LABEL[p]}
        </button>
      ))}
      <p className="muted small center">{txt.weOnlyUseThis}</p>
    </div>
  );
}

export function ProviderIcon({ p }: { p: AuthProvider }) {
  if (p === "google") return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="#4285F4" d="M21.6 12.2c0-.7-.1-1.3-.2-1.9H12v3.7h5.4a4.6 4.6 0 0 1-2 3v2.5h3.2c1.9-1.7 3-4.3 3-7.3z"/><path fill="#34A853" d="M12 22c2.7 0 5-.9 6.6-2.4l-3.2-2.5c-.9.6-2 1-3.4 1-2.6 0-4.8-1.8-5.6-4.1H3.1v2.6A10 10 0 0 0 12 22z"/><path fill="#FBBC05" d="M6.4 13.9a6 6 0 0 1 0-3.8V7.5H3.1a10 10 0 0 0 0 9l3.3-2.6z"/><path fill="#EA4335" d="M12 5.9c1.5 0 2.8.5 3.8 1.5l2.8-2.8A10 10 0 0 0 3.1 7.5l3.3 2.6C7.2 7.7 9.4 5.9 12 5.9z"/></svg>
  );
  if (p === "apple") return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="currentColor"><path d="M16.4 12.7c0-2.4 2-3.5 2.1-3.6-1.1-1.7-2.9-1.9-3.5-1.9-1.5-.2-2.9.9-3.7.9-.8 0-1.9-.9-3.2-.8-1.6 0-3.1 1-4 2.4-1.7 3-.4 7.3 1.2 9.7.8 1.2 1.8 2.5 3 2.4 1.2 0 1.7-.8 3.2-.8s1.9.8 3.2.8c1.3 0 2.2-1.2 3-2.4.9-1.4 1.3-2.7 1.3-2.8-.1 0-2.6-1-2.6-3.9zM14 5.6c.7-.8 1.1-2 1-3.1-1 0-2.2.7-2.9 1.5-.6.7-1.2 1.9-1 3 1.1.1 2.2-.6 2.9-1.4z"/></svg>
  );
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="currentColor"><path d="M19.6 5.3A17 17 0 0 0 15.4 4l-.5 1a16 16 0 0 0-5.8 0l-.5-1a17 17 0 0 0-4.2 1.3C1.7 9.3 1 13.2 1.4 17a17 17 0 0 0 5.2 2.6l1.1-1.8a11 11 0 0 1-1.7-.8l.4-.3a12 12 0 0 0 11.2 0l.4.3-1.7.8 1.1 1.8a17 17 0 0 0 5.2-2.6c.5-4.4-.8-8.3-3-11.7zM8.7 14.7c-1 0-1.9-1-1.9-2.1s.8-2.1 1.9-2.1 1.9 1 1.9 2.1-.8 2.1-1.9 2.1zm6.6 0c-1 0-1.9-1-1.9-2.1s.8-2.1 1.9-2.1 1.9 1 1.9 2.1-.8 2.1-1.9 2.1z"/></svg>
  );
}

/**
 * Profile card: which providers open this account, Link for the rest, Remove
 * (with a word first) as long as another remains. `justLinked` is the ?linked=
 * provider we came back with.
 */
export function WaysToSignIn({ me, justLinked }: { me: Me; justLinked?: string }) {
  const qc = useQueryClient();
  const health = useHealth();
  const offered: AuthProvider[] = health.data?.providers ?? ["discord"];
  const [removing, setRemoving] = useState<AuthProvider | null>(null);
  const [busy, setBusy] = useState<AuthProvider | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const unlink = useMutation({
    mutationFn: (p: AuthProvider) => unlinkProvider(me.accountRows.find((r) => r.provider === p)!.id).then((r) => { if (r.error) throw r.error; }),
    onSuccess: async () => { setRemoving(null); await qc.invalidateQueries({ queryKey: ["me"] }); },
  });
  const link = async (p: AuthProvider) => {
    setErr(null); setBusy(p);
    const r = await linkProvider(p).catch((e: unknown) => ({ error: e }));
    if (r && "error" in r && r.error) { setErr(describeError(r.error)); setBusy(null); }
  };
  // "email" is the email-code sign-in: shown as its own row, managed through the addresses below, never removed here.
  const providers = me.accounts.filter((a) => (a as string) !== "email");
  const viaEmail = me.accounts.some((a) => (a as string) === "email");
  const linked = new Set(providers);
  const rows: AuthProvider[] = [...providers, ...offered.filter((p) => !linked.has(p))];
  return (
    <section className="card stack-sm ways">
      <div><div className="k" style={{ fontWeight: 500 }}>{txt.waysToSignIn}</div>
        <div className="muted small">{txt.anyOfTheseOpens}{providers[0] ? <>{txt.orCanUseThe(PROVIDER_LABEL[providers[0]])}</> : null}.</div></div>
      {justLinked && linked.has(justLinked as AuthProvider) && <p className="toast small"><b>{txt.isLinked(PROVIDER_LABEL[justLinked as AuthProvider])}</b>{txt.nextTimeEitherButton}</p>}
      <div className="prows">
        {rows.map((p) => (
          <div key={p} className="prow">
            <span className={`pi ${p}`}><ProviderIcon p={p} /></span>
            <div className="t">{PROVIDER_LABEL[p]}<small>{linked.has(p) ? txt.linked : txt.notLinked}</small></div>
            {linked.has(p)
              ? me.accounts.length <= 1
                ? <span className="muted small">{txt.onlyWayIn}</span>
                : <button type="button" className="linkish small muted" onClick={() => setRemoving(p)}>{txt.remove}</button>
              : <button type="button" className="btn sm" disabled={busy === p} onClick={() => void link(p)}>{busy === p ? txt.opening : txt.link}</button>}
          </div>
        ))}
        {viaEmail && (
          <div className="prow">
            <span className="pi email"><MailIcon width={16} height={16} /></span>
            <div className="t">{txt.emailCode}<small>{txt.withAnyConfirmedAddress}</small></div>
          </div>
        )}
      </div>
      {removing && (
        <div className="confirm">
          <p className="small">{txt.removeYouDSign(PROVIDER_LABEL[removing], me.accounts.filter((a) => a !== removing).map((a) => PROVIDER_LABEL[a] ?? txt.anEmailCode).join(txt.orSep))}</p>
          <div className="row"><button type="button" className="btn danger sm" disabled={unlink.isPending} onClick={() => unlink.mutate(removing)}>{txt.remove2}</button><button type="button" className="btn ghost sm" onClick={() => setRemoving(null)}>{txt.keep}</button></div>
        </div>
      )}
      {(err || unlink.isError) && <p className="error small">{err ?? describeError(unlink.error)}</p>}
    </section>
  );
}

/** For a brand-new empty account: the way back to the one they already have. */
export function WrongDoorNote({ me }: { me: Me }) {
  const used = PROVIDER_LABEL[me.accounts[0] ?? "discord"] ?? txt.anEmailCode;
  return (
    <p className="toast quiet-toast small">
      <b>{txt.thisAccountIsBrand}</b>{txt.ifYouAlreadyUse(used)}</p>
  );
}
